import type { PlainButtonProps } from '@customerio/pluma-components/react';
import {
  Button,
  Icon,
  PlainButton,
  Spinner,
  Tooltip,
  TooltipContent,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import clsx from 'clsx';
import { forwardRef } from 'react';
import * as styles from './index.css';

import { mergeRefs } from 'react-merge-refs';

/**
 * Icon Button component for the top navigation actions.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    icon?: IconName;
    label: string;
    tooltip?: React.ReactNode;
    isLoading?: boolean;
    children?: React.ReactNode;
    className?: string;
    showLabel?: boolean;
    isNewLayout?: boolean;
  } & PlainButtonProps
>(function IconButton(
  { icon, label, tooltip, isLoading, children, className, showLabel = false, isNewLayout, ...rest },
  ref,
) {
  if (isNewLayout === true) {
    return (
      <Tooltip isDisabled={tooltip == null} isCustom={true}>
        {(context) => (
          <>
            <Button
              ref={mergeRefs([ref, context.reference.setReference])}
              icon={icon}
              isIconOnly={icon != null && showLabel !== true}
              aria-label={showLabel ? undefined : label}
              variant="subtle"
              size="sm"
              isLoading={isLoading}
              {...context.reference.getReferenceProps(rest as any)}
            >
              {showLabel ? label : null}
              {children}
            </Button>
            <TooltipContent>{tooltip}</TooltipContent>
          </>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip isDisabled={tooltip == null} isCustom={true}>
      {(context) => (
        <>
          <PlainButton
            ref={mergeRefs([ref, context.reference.setReference])}
            className={clsx(styles.iconButton, showLabel && styles.iconButtonWithLabel, className)}
            {...context.reference.getReferenceProps(rest as any)}
          >
            {showLabel ? null : <VisuallyHidden>{label}</VisuallyHidden>}

            {icon != null ? (
              <div className={styles.iconButtonIcon}>
                {isLoading === true ? <Spinner color="invert" /> : <Icon name={icon} size="md" sizeVersion="v2" />}
              </div>
            ) : null}
            {showLabel ? <span className={styles.iconButtonLabel}>{label}</span> : null}
            {children}
          </PlainButton>
          <TooltipContent>{tooltip}</TooltipContent>
        </>
      )}
    </Tooltip>
  );
});
